// components/ZonesView.js - Vista de mapa de zonas con branding actualizado
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ZONAS_POR_CP } from '../utils/zonas';

const ZonesView = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zonesData, setZonesData] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);

  // Colores de zonas corporativos (rojo, gris, negro)
  const zoneColors = {
    "Zona 1-2": '#DC2626', // Rojo principal
    "Zona 3": '#374151',   // Gris oscuro
    "Zona 4": '#6B7280',   // Gris medio
    "Zona 5": '#991B1B',   // Rojo oscuro
    "Zona 6": '#1F2937'    // Gris muy oscuro/negro
  };

  // Definir ubicaciones exactas según la imagen proporcionada
  const zoneLocations = {
    "Zona 1-2": { lat: 37.65, lng: -0.95, radius: 25000 }, // ZONA 2 (rojo): San Pedro del Pinatar, Los Alcázares
    "Zona 3": { lat: 37.95, lng: -0.65, radius: 20000 },   // ZONA 3 (gris oscuro): Torrevieja, La Zenia  
    "Zona 4": { lat: 38.05, lng: -0.85, radius: 18000 },   // ZONA 4 (gris medio): Orihuela área norte
    "Zona 5": { lat: 37.85, lng: -1.15, radius: 22000 },   // ZONA 5 (rojo oscuro): Murcia centro-oeste
    "Zona 6": { lat: 38.15, lng: -1.25, radius: 28000 }    // ZONA 6 (negro): Molina de Segura, Abarán, norte
  };

  const zonasPor4Digitos = ZONAS_POR_CP;

  // Geocodificar código postal
  const geocodePostalCode = async (postalCode) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(postalCode + ", España")}&limit=1`,
        {
          headers: { 
            'User-Agent': 'ClimatizacionZapata/1.0 (contacto@climatizacionzapata.es)' 
          }
        }
      );
      const data = await response.json();
      
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          postalCode: postalCode
        };
      }
      return null;
    } catch (error) {
      console.error(`Error geocodificando ${postalCode}:`, error);
      return null;
    }
  };

  // Agrupar códigos postales por zona con colores correctos
  const groupPostalCodesByZone = () => {
    const zoneGroups = {};
    
    Object.entries(zonasPor4Digitos).forEach(([postalPrefix, { zona, tecnico }]) => {
      if (!zoneGroups[zona]) {
        zoneGroups[zona] = {
          zona,
          tecnico,
          color: zoneColors[zona], // Usar colores por zona, no por técnico
          postalCodes: [],
          location: zoneLocations[zona] // Ubicación predefinida
        };
      }
      zoneGroups[zona].postalCodes.push(postalPrefix);
    });
    
    return Object.values(zoneGroups);
  };

  // Inicializar mapa y cargar datos
  useEffect(() => {
    const loadZonesData = async () => {
      setLoading(true);
      setError("");

      try {
        console.log("🗺️ Inicializando mapa de zonas...");
        
        // Cargar Leaflet dinámicamente
        if (!window.L) {
          const leafletCSS = document.createElement('link');
          leafletCSS.rel = 'stylesheet';
          leafletCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(leafletCSS);

          const leafletJS = document.createElement('script');
          leafletJS.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          document.head.appendChild(leafletJS);

          await new Promise((resolve) => {
            leafletJS.onload = resolve;
          });
        }

        // Agrupar zonas
        const zones = groupPostalCodesByZone();
        console.log("📍 Zonas agrupadas:", zones);

        setZonesData(zones);
        console.log("✅ Zonas configuradas:", zones);

        // Inicializar mapa centrado en la región de Murcia con zoom más amplio
        if (mapRef.current && !mapInstanceRef.current) {
          const map = window.L.map(mapRef.current).setView([37.85, -1.05], 9);
          
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(map);

          // Añadir círculos por zona con mayor cobertura
          zones.forEach((zone, index) => {
            if (zone.location) {
              // Círculo principal más grande para cubrir toda el área
              const circle = window.L.circle([zone.location.lat, zone.location.lng], {
                color: zone.color,
                fillColor: zone.color,
                fillOpacity: 0.25,
                weight: 2,
                radius: zone.location.radius
              }).addTo(map);

              // Círculo interno más intenso
              const innerCircle = window.L.circle([zone.location.lat, zone.location.lng], {
                color: zone.color,
                fillColor: zone.color,
                fillOpacity: 0.4,
                weight: 1,
                radius: zone.location.radius * 0.6
              }).addTo(map);

              // Marcador en el centro con mejor visibilidad
              const marker = window.L.marker([zone.location.lat, zone.location.lng], {
                icon: window.L.divIcon({
                  className: 'custom-zone-marker',
                  html: `<div style="
                    background: ${zone.color}; 
                    color: white; 
                    padding: 8px 16px; 
                    border-radius: 20px; 
                    font-weight: bold; 
                    font-size: 14px;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                    border: 3px solid white;
                    text-align: center;
                    white-space: nowrap;
                  ">${zone.zona}</div>`,
                  iconSize: [100, 36],
                  iconAnchor: [50, 18]
                })
              }).addTo(map);

              // Popup con información detallada
              const popupContent = `
                <div style="min-width: 240px;">
                  <h3 style="margin: 0 0 12px 0; color: ${zone.color}; font-weight: bold; font-size: 18px;">${zone.zona}</h3>
                  <p style="margin: 8px 0;"><strong>Técnico responsable:</strong> ${zone.tecnico}</p>
                  <p style="margin: 8px 0;"><strong>Códigos postales:</strong> ${zone.postalCodes.length} rangos cubiertos</p>
                  <div style="margin: 12px 0;">
                    <strong style="font-size: 13px; color: #666;">Códigos postales incluidos:</strong>
                    <div style="margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px; max-height: 100px; overflow-y: auto;">
                      ${zone.postalCodes.map(code => 
                        `<span style="background: ${zone.color}15; color: ${zone.color}; font-size: 11px; padding: 3px 7px; border-radius: 10px; font-weight: 600; border: 1px solid ${zone.color}30;">${code}*</span>`
                      ).join('')}
                    </div>
                  </div>
                  <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
                    <strong>Cobertura:</strong> Área aproximada de servicio
                  </div>
                </div>
              `;

              marker.bindPopup(popupContent);
              circle.bindPopup(popupContent);
              innerCircle.bindPopup(popupContent);

              // Eventos
              marker.on('click', () => setSelectedZone(zone));
              circle.on('click', () => setSelectedZone(zone));
              innerCircle.on('click', () => setSelectedZone(zone));
            }
          });

          mapInstanceRef.current = map;
        }

      } catch (err) {
        console.error("❌ Error cargando mapa de zonas:", err);
        setError(`Error cargando mapa: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadZonesData();

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="bg-white rounded-2xl shadow-lg border p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            🗺️ Mapa de Zonas
            {loading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
            )}
          </h2>
          <p className="text-gray-600 mt-1">
            Distribución territorial por técnico y códigos postales
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
          ❌ {error}
        </div>
      )}

      {/* Leyenda */}
      <div className="bg-gray-50 p-4 rounded-xl mb-6">
        <h3 className="font-semibold text-gray-800 mb-3">👥 Técnicos y Zonas</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {Object.entries(zoneColors).map(([zona, color]) => {
            const zoneData = zonesData.find(z => z.zona === zona);
            
            return (
              <div key={zona} className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                <div 
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: color }}
                ></div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-800">{zona}</div>
                  <div className="text-xs text-gray-500">
                    {zoneData?.tecnico || 'N/A'}
                    ({zoneData?.postalCodes.length || 0} CP)
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className="bg-red-50 border border-red-200 p-6 rounded-xl mb-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-red-800 font-medium">Cargando mapa de zonas...</p>
          <p className="text-red-600 text-sm mt-1">Geocodificando códigos postales y creando zonas</p>
        </div>
      )}

      {/* Mapa */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Mapa principal */}
        <div className="lg:col-span-2">
          <div 
            ref={mapRef} 
            className="w-full h-[500px] rounded-xl border border-gray-200 bg-gray-100"
            style={{ minHeight: '500px' }}
          />
          
          {/* Información del mapa */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border text-sm text-gray-600">
            <div className="flex justify-between items-center">
              <span>🔍 Haz clic en las zonas para más información</span>
              <span className="text-xs">📡 Datos de OpenStreetMap</span>
            </div>
          </div>
        </div>

        {/* Panel lateral con información */}
        <div className="space-y-4">
          {/* Zona seleccionada */}
          {selectedZone ? (
            <div className="bg-white border rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-lg mb-3" style={{ color: selectedZone.color }}>
                {selectedZone.zona}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Técnico:</span>
                  <span className="font-medium">{selectedZone.tecnico}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Códigos postales:</span>
                  <span className="font-medium">{selectedZone.postalCodes.length} rangos</span>
                </div>
                <div className="mt-3">
                  <span className="text-gray-600 text-xs">Ejemplos de códigos:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedZone.postalCodes.slice(0, 6).map(code => (
                      <span key={code} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {code}*
                      </span>
                    ))}
                    {selectedZone.postalCodes.length > 6 && (
                      <span className="text-xs text-gray-500">
                        +{selectedZone.postalCodes.length - 6} más
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border rounded-xl p-4 text-center text-gray-500">
              <div className="text-4xl mb-2">🗺️</div>
              <p className="text-sm">Selecciona una zona en el mapa para ver detalles</p>
            </div>
          )}

          {/* Estadísticas */}
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-800 mb-3">📊 Resumen</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total zonas:</span>
                <span className="font-medium">{zonesData.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Códigos postales:</span>
                <span className="font-medium">
                  {Object.keys(ZONAS_POR_CP).length} rangos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Técnicos activos:</span>
                <span className="font-medium">4</span>
              </div>
            </div>
          </div>

          {/* Instrucciones */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <h4 className="font-semibold text-red-800 mb-2">ℹ️ Cómo usar</h4>
            <ul className="text-xs text-red-700 space-y-1">
              <li>• Los círculos representan las zonas de servicio</li>
              <li>• Cada color corresponde a un técnico</li>
              <li>• Haz clic en una zona para ver detalles</li>
              <li>• Los códigos postales se agrupan por los primeros 4 dígitos</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Informe de cobertura */}
      <InformeCobertura />
    </div>
  );
};

const ZONE_COLORS_TW = {
  "Zona 1-2": { bg: "bg-red-100",  text: "text-red-800",  border: "border-red-200"  },
  "Zona 3":   { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-300" },
  "Zona 4":   { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
  "Zona 5":   { bg: "bg-amber-100",text: "text-amber-800",border: "border-amber-200"},
  "Zona 6":   { bg: "bg-purple-100",text:"text-purple-800",border:"border-purple-200"},
};

function InformeCobertura() {
  const [expandida, setExpandida] = useState(null);

  const porZona = useMemo(() => {
    const grupos = {};
    Object.entries(ZONAS_POR_CP).forEach(([prefix, { zona, tecnico }]) => {
      if (!grupos[zona]) grupos[zona] = { tecnico, prefixes: [] };
      grupos[zona].prefixes.push(prefix);
    });
    Object.values(grupos).forEach(g => g.prefixes.sort());
    return grupos;
  }, []);

  const gaps = useMemo(() => {
    const result = {};
    Object.entries(porZona).forEach(([zona, { prefixes }]) => {
      const zonaGaps = [];
      for (let i = 0; i < prefixes.length - 1; i++) {
        const curr = parseInt(prefixes[i], 10);
        const next = parseInt(prefixes[i + 1], 10);
        if (next - curr > 1) {
          zonaGaps.push({ after: prefixes[i], before: prefixes[i + 1], n: next - curr - 1 });
        }
      }
      if (zonaGaps.length) result[zona] = zonaGaps;
    });
    return result;
  }, [porZona]);

  const totalGaps = Object.values(gaps).reduce((s, g) => s + g.length, 0);

  return (
    <div className="mt-8 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">Informe de cobertura</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {Object.keys(ZONAS_POR_CP).length} rangos asignados · {totalGaps === 0 ? 'sin huecos detectados' : `${totalGaps} posible${totalGaps > 1 ? 's' : ''} hueco${totalGaps > 1 ? 's' : ''}`}
          </p>
        </div>
        {totalGaps > 0 && (
          <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
            ⚠ {totalGaps} huecos
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-100">
        {Object.entries(porZona).sort(([a], [b]) => a.localeCompare(b)).map(([zona, { tecnico, prefixes }]) => {
          const tw = ZONE_COLORS_TW[zona] || ZONE_COLORS_TW["Zona 5"];
          const zonaGaps = gaps[zona] || [];
          const abierta = expandida === zona;

          return (
            <div key={zona}>
              <button
                onClick={() => setExpandida(abierta ? null : zona)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${tw.bg} border ${tw.border}`} />
                  <span className="font-medium text-sm text-gray-800">{zona}</span>
                  <span className="text-xs text-gray-500">{tecnico}</span>
                </div>
                <div className="flex items-center gap-2">
                  {zonaGaps.length > 0 && (
                    <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                      ⚠ {zonaGaps.length} hueco{zonaGaps.length > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{prefixes.length} rangos</span>
                  <span className="text-gray-400 text-xs">{abierta ? '▲' : '▼'}</span>
                </div>
              </button>

              {abierta && (
                <div className="px-5 pb-4 bg-gray-50">
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {prefixes.map((p, i) => {
                      const prevNum = i > 0 ? parseInt(prefixes[i - 1], 10) : null;
                      const currNum = parseInt(p, 10);
                      const hayHuecoAntes = prevNum !== null && currNum - prevNum > 1;
                      return (
                        <React.Fragment key={p}>
                          {hayHuecoAntes && (
                            <span className="text-xs text-amber-600 self-center px-1">···</span>
                          )}
                          <span className={`font-mono text-xs px-2 py-1 rounded border ${tw.bg} ${tw.text} ${tw.border}`}>
                            {p}*
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {zonaGaps.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {zonaGaps.map((g, i) => (
                        <p key={i} className="text-xs text-amber-700">
                          ⚠ Entre {g.after}* y {g.before}*: {g.n} rango{g.n > 1 ? 's' : ''} sin asignar
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ZonesView;